/**
 * Safari/iOS auth-cookie hardening.
 *
 * Safari's Intelligent Tracking Prevention (ITP) and stricter SameSite
 * cookie rules sometimes silently drop our `session_token` cookie even
 * when CORS + cookie attributes are spec-compliant. When that happens
 * the user logs in successfully (sees the success toast), navigates to
 * the dashboard, but `/api/auth/me` returns 401 because the cookie
 * never made it past Safari → they bounce back to /login.
 *
 * This module patches `window.fetch` once at app boot to ALSO send the
 * session token as `Authorization: Bearer …` whenever we have one in
 * localStorage. The backend already accepts both cookie OR bearer for
 * authentication (see `get_current_user`) so this is a pure additive
 * belt-and-suspenders fallback — no other code needs to change.
 *
 * Flow:
 *   1. Login.jsx stores `session_token` in localStorage on success.
 *   2. Every subsequent fetch to the backend automatically attaches
 *      `Authorization: Bearer <token>` if no Authorization header is
 *      already present.
 *   3. Logout clears the token.
 *
 * Chrome/Firefox already work fine via cookies — the Authorization
 * header is harmless redundancy for them, and a lifesaver on Safari.
 */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'session_token';

let originalFetch = null;

function shouldAttachToken(url) {
  if (!url) return false;
  const u = String(url);
  // Only attach to our backend's /api requests — never leak to 3rd-party APIs.
  return u.startsWith(BACKEND_URL) || u.startsWith('/api');
}

export function installAuthFetchInterceptor() {
  if (originalFetch) return;  // idempotent
  originalFetch = window.fetch.bind(window);

  window.fetch = (input, init = {}) => {
    const url = typeof input === 'string' ? input : input?.url;
    if (!shouldAttachToken(url)) {
      return originalFetch(input, init);
    }
    const token = (() => {
      try { return localStorage.getItem(TOKEN_KEY); } catch (_e) { return null; }
    })();
    if (!token) return originalFetch(input, init);

    // Merge headers — preserve any caller-supplied Authorization.
    const headers = new Headers(init.headers || {});
    if (!headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return originalFetch(input, { ...init, headers });
  };
}

export function persistSessionToken(token) {
  try { if (token) localStorage.setItem(TOKEN_KEY, token); } catch (_e) { /* ignore quota errors */ }
}

export function clearSessionToken() {
  try { localStorage.removeItem(TOKEN_KEY); } catch (_e) { /* ignore */ }
}

/**
 * Cross-partition auth handoff for links opened in a NEW TAB.
 *
 * When the app runs inside an embedded/iframe context (e.g. the platform
 * preview pane), browsers PARTITION cookies + localStorage. A tab spawned
 * with window.open / target=_blank gets the FIRST-PARTY partition, which
 * either has no session (→ bounced to /login) or a stale session from a
 * previously logged-in user (→ wrong workshop, 404s on print/invoices).
 *
 * Fix: append the current session token (+ super-admin site selection) as
 * a URL hash; the new tab adopts it at boot BEFORE any auth check runs.
 * The hash is never sent to the server and is stripped immediately.
 */
const SITE_KEY = 'asf_selected_site_id';

export function withSessionHash(path) {
  try {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) return path;
    let h = `st=${encodeURIComponent(t)}`;
    const sid = localStorage.getItem(SITE_KEY);
    if (sid) h += `&ssid=${encodeURIComponent(sid)}`;
    return path + (path.includes('#') ? '&' : '#') + h;
  } catch (_e) {
    return path;
  }
}

export function adoptUrlSessionToken() {
  try {
    const hash = window.location.hash || '';
    const st = hash.match(/[#&]st=([^&]+)/);
    if (!st) return;
    localStorage.setItem(TOKEN_KEY, decodeURIComponent(st[1]));
    const ssid = hash.match(/[#&]ssid=([^&]+)/);
    if (ssid) localStorage.setItem(SITE_KEY, decodeURIComponent(ssid[1]));
    const cleaned = hash
      .replace(/[#&]st=[^&]*/, '')
      .replace(/[#&]ssid=[^&]*/, '')
      .replace(/^&/, '#');
    const url = window.location.pathname + window.location.search + (cleaned && cleaned !== '#' ? cleaned : '');
    window.history.replaceState(null, '', url);
  } catch (_e) { /* noop */ }
}
